import { useMemo } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";

// Components
import TournamentTerms from "components/organisms/Tournament/TournamentTerms";
import PageTitle from "components/atoms/PageTitle";

// Containers
import GameListContainer from "containers/organisms/GameListContainer";

// Hooks
import useTranslate from "hooks/useTranslate";
import { useBreakpoint } from "hooks/useBreakpoint";

// Types
import { TournamentDataType } from "types/Tournament";
import { GameType } from "types/Games";
import url from "url/index";
import useScrollDetection from "../../../hooks/useScrollDetection";
import TournamentPrize from "./TournamentPrize";
import TournamentBlock from "./TournamentBlock";
import TournamentBoard from "./TournamentBoard";
import TournamentRules from "./TournamentRules";

import "./styles.sass";

type TournamentProps = {
  games: GameType[] | null;
  tournament: TournamentDataType;
  lang: string;
  isAuthenticated: boolean | null;
};

const Tournament = (props: TournamentProps) => {
  const {
    header,
    image,
    rules,
    prizePool,
    minBet,
    description,
    endDate,
    startDate,
    totalPrizePool,
    // eslint-disable-next-line react/destructuring-assignment
  } = props.tournament;
  const { tournament, isAuthenticated } = props;
  const { lang, games } = props;
  const navigate = useNavigate();
  const { isDesktop } = useBreakpoint();
  const t = useTranslate();
  const isScrolled = useScrollDetection();

  const prizePlaces = prizePool?.length.toString();
  const period = `${dayjs(startDate).format("DD.MM.YYYY")} - ${dayjs(endDate).format("DD.MM.YYYY")}`;

  const sortedPrizePool = useMemo(() => {
    return prizePool ? [...prizePool].sort((a, b) => a.place - b.place) : null;
  }, [prizePool]);

  const TournamentHeader = classNames("Tournament__header", {
    notAuth: !isAuthenticated,
    scroll: isScrolled,
  });
  const TournamentBlockClass = classNames("Tournament__block", {
    notAuth: !isAuthenticated,
  });

  return (
    <div className="Tournament">
      <div className={TournamentHeader}>
        <PageTitle
          title="page.tournament.header"
          backText={isDesktop ? "popup.payment.error.back" : ""}
          onBack={() => navigate(url.routes.tournament.index())}
        />
      </div>
      <div className={TournamentBlockClass}>
        <TournamentBlock
          games={games}
          image={image}
          name={header}
          description={description}
          lang={lang}
          tournament={tournament}
        />
      </div>
      <div className="Tournament__prize">
        <TournamentPrize totalPrizePool={totalPrizePool} endDate={endDate} />
      </div>
      <div className="Tournament__game-list">
        <div className="Tournament__title">
          {t("tournament.view.game_list")}
        </div>
        <GameListContainer />
      </div>
      {prizePlaces && (
        <div className="Tournament__rules">
          <TournamentRules
            prizePools={prizePlaces}
            minBet={minBet}
            period={period}
          />
        </div>
      )}
      {sortedPrizePool && (
        <div className="Tournament__board">
          <TournamentBoard prizes={sortedPrizePool} />
        </div>
      )}
      <div className="Tournament__terms">
        <TournamentTerms rules={rules} />
      </div>
    </div>
  );
};

export default Tournament;
